import { useEffect, useMemo, useState } from 'react';
import { Bell, CheckCircle2, CircleSlash, Info, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  useMyNotificationPreferences,
  useUpdateNotificationPreferences,
  useSelectAllNotificationPreferences,
  useDeselectAllNotificationPreferences,
  groupByCategory,
  categoryLabel,
  type NotificationTypeWithPreference,
  type UpdatePreferenceItem,
} from '@/api/extensions/useNotificationPreferences';
import { useToast } from '@/contexts/ToastContext';

/**
 * Notifications tab on the profile page.
 *
 * UX shape:
 *   - One section per category (buddy groups, partners, payments, …)
 *   - One Switch per notification type, with description hover/sub-text
 *   - Required types render disabled with a "Required" pill — they're
 *     informational only (the backend forces them on regardless)
 *   - "Select all" / "Deselect all" toggles every non-required type at once
 *   - Local pending state on each switch so the click feels instant; if the
 *     mutation fails we revert and show a toast.
 */
export function NotificationsTab() {
  const { data, isLoading, error } = useMyNotificationPreferences();
  const updateMut = useUpdateNotificationPreferences();
  const selectAllMut = useSelectAllNotificationPreferences();
  const deselectAllMut = useDeselectAllNotificationPreferences();
  const { showSuccess, showError } = useToast();

  // Optimistic state for currently-in-flight rows. Keys are notification
  // type keys; values are the desired enabled state. Once the mutation
  // resolves we drop the entry from the map and let the server data win.
  const [pending, setPending] = useState<Record<string, boolean>>({});

  // Clear pending entries that the server has now confirmed (or rejected
  // by virtue of returning a different value).
  useEffect(() => {
    if (!data) return;
    setPending((prev) => {
      const next: Record<string, boolean> = {};
      for (const [key, desired] of Object.entries(prev)) {
        const row = data.find((r) => r.key === key);
        if (row && row.enabled !== desired) {
          // Server hasn't applied this yet (and we haven't gotten the
          // failure callback either) — keep tracking it.
          next[key] = desired;
        }
      }
      return next;
    });
  }, [data]);

  const grouped = useMemo(() => (data ? groupByCategory(data) : []), [data]);

  const totalToggleable = data?.filter((r) => !r.is_required).length ?? 0;
  const totalEnabled = data?.filter((r) => !r.is_required && r.enabled).length ?? 0;
  const allOn = totalToggleable > 0 && totalEnabled === totalToggleable;
  const allOff = totalToggleable > 0 && totalEnabled === 0;

  const handleToggle = (row: NotificationTypeWithPreference, next: boolean) => {
    if (row.is_required) return;
    setPending((p) => ({ ...p, [row.key]: next }));

    const item: UpdatePreferenceItem = { key: row.key, enabled: next };
    updateMut.mutate([item], {
      onError: () => {
        // Drop optimistic entry so the row snaps back to the server value.
        setPending((p) => {
          const { [row.key]: _drop, ...rest } = p;
          return rest;
        });
        showError(
          'Could not save preference',
          'Try again — the change was not saved.',
        );
      },
      onSuccess: () => {
        // No toast on every flip — would be noisy. The visual state change
        // is enough confirmation.
      },
    });
  };

  const handleSelectAll = () => {
    selectAllMut.mutate(undefined, {
      onSuccess: () => showSuccess('All email notifications enabled'),
      onError: () =>
        showError('Could not enable all', 'Try again — no changes were saved.'),
    });
  };

  const handleDeselectAll = () => {
    deselectAllMut.mutate(undefined, {
      onSuccess: () => showSuccess('All optional emails disabled'),
      onError: () =>
        showError('Could not disable all', 'Try again — no changes were saved.'),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Could not load notification preferences. Refresh the page to try again.
          </p>
        </CardContent>
      </Card>
    );
  }

  const bulkBusy = selectAllMut.isPending || deselectAllMut.isPending;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Email notifications
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Choose which events trigger an email to{' '}
            <span className="font-medium text-foreground">your registered email</span>.
            New notification types added later default to ON — flip them off here
            any time.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
            <p className="text-xs text-muted-foreground">
              {totalEnabled} of {totalToggleable} optional notifications enabled.
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSelectAll}
                disabled={bulkBusy || allOn || totalToggleable === 0}
              >
                {selectAllMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                Select all
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDeselectAll}
                disabled={bulkBusy || allOff || totalToggleable === 0}
              >
                {deselectAllMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <CircleSlash className="h-3.5 w-3.5 mr-1.5" />
                )}
                Deselect all
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {grouped.map(({ category, items }) => (
        <Card key={category}>
          <CardHeader>
            <CardTitle className="text-base">{categoryLabel(category)}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {items.map((row) => {
                const desired = pending[row.key];
                const checked = row.is_required
                  ? true
                  : desired !== undefined
                  ? desired
                  : row.enabled;
                const switchId = `notif-${row.key}`;
                return (
                  <li
                    key={row.key}
                    className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
                  >
                    <div className="min-w-0 flex-1">
                      <label
                        htmlFor={switchId}
                        className="flex items-center gap-2 text-sm font-medium text-foreground cursor-pointer"
                      >
                        {row.label}
                        {row.is_required && (
                          <Badge
                            variant="outline"
                            className="text-[10px] uppercase tracking-wide font-normal text-muted-foreground"
                          >
                            Required
                          </Badge>
                        )}
                      </label>
                      {row.description && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {row.description}
                        </p>
                      )}
                    </div>
                    <Switch
                      id={switchId}
                      checked={checked}
                      onCheckedChange={(next) => handleToggle(row, next)}
                      disabled={row.is_required || bulkBusy}
                    />
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <p>
              We always send transactional emails for account security
              (password resets, email verification). These can't be disabled
              from this page.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
